import { AVIATIONSTACK_BASE_URL, AVIATIONSTACK_KEY } from "../../config/api";

const CACHE_KEY = "th_aviationstack_cache_v1";
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;
const inflight = new Map();

const INVALID_FLIGHT = /^(tbc|tba|n\/?a|none|unknown|dontknow|donotknow)$/i;

export const normalizeFlightIata = (raw) => {
  if (!raw) return null;
  const compact = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!compact || INVALID_FLIGHT.test(compact)) return null;
  const match = compact.match(/^([A-Z]{2,3})(\d{1,4}[A-Z]?)$/);
  if (!match) return null;
  const airline = match[1];
  const number = match[2].replace(/^0+(?=\d)/, "");
  return `${airline}${number}`;
};

const readCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeCache = (cache) => {
  localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
};

const isFresh = (entry) => entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS;

const londonParts = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(date).map((part) => [part.type, part.value]));
  return {
    dateKey: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    label: `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`,
  };
};

const pickHeathrowFlight = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return null;
  return (
    rows.find(
      (row) =>
        row?.arrival?.iata === "LHR" ||
        row?.arrival?.icao === "EGLL" ||
        String(row?.arrival?.airport || "").toLowerCase().includes("heathrow")
    ) || rows[0]
  );
};

const toStatus = (flight) => {
  if (!flight) return { ok: false };
  const arrivalIso =
    flight.arrival?.estimated ||
    flight.arrival?.actual ||
    flight.arrival?.scheduled ||
    null;
  const london = londonParts(arrivalIso);
  return {
    ok: true,
    status: String(flight.flight_status || "").toLowerCase(),
    landed: String(flight.flight_status || "").toLowerCase() === "landed",
    arrivalIso,
    london,
    terminal: flight.arrival?.terminal || null,
    baggage: flight.arrival?.baggage || null,
  };
};

const fetchOneFlight = async (iata) => {
  const url = `${AVIATIONSTACK_BASE_URL}?access_key=${encodeURIComponent(
    AVIATIONSTACK_KEY
  )}&flight_iata=${encodeURIComponent(iata)}&limit=1`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`AviationStack ${response.status}`);
  const json = await response.json();
  if (json?.error) throw new Error(json.error?.info || json.error?.message || "AviationStack error");
  return toStatus(pickHeathrowFlight(json?.data));
};

export const getCachedFlight = (iata) => {
  const entry = readCache()[iata];
  return isFresh(entry) ? entry.result : null;
};

export const fetchFlightsByIata = async (iatas, { force = false } = {}) => {
  const unique = [...new Set((iatas || []).filter(Boolean))];
  const cache = readCache();
  const results = {};
  const missing = [];

  unique.forEach((iata) => {
    if (!force && isFresh(cache[iata])) {
      results[iata] = cache[iata].result;
    } else {
      missing.push(iata);
    }
  });

  await Promise.all(
    missing.map(async (iata) => {
      if (!force && inflight.has(iata)) {
        results[iata] = await inflight.get(iata);
        return;
      }
      const request = fetchOneFlight(iata)
        .catch(() => ({ ok: false }))
        .then((result) => {
          const next = readCache();
          next[iata] = { fetchedAt: Date.now(), result };
          writeCache(next);
          return result;
        });
      inflight.set(iata, request);
      try {
        results[iata] = await request;
      } finally {
        inflight.delete(iata);
      }
    })
  );

  return {
    results,
    fetched: missing.length,
    cached: unique.length - missing.length,
  };
};
